/**
 * =============================================================================
 * IDS CRM Web - Dashboard Page
 * =============================================================================
 *
 * PURPOSE:
 * Main dashboard showing key metrics and recent activity.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import { formatDateKey } from '../utils/dates';
import WorldClocks from '../components/WorldClocks';
import './Dashboard.css';

function Dashboard() {
  const { authFetch, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [recentOpportunities, setRecentOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, opportunitiesRes, watchlistRes, watchlistSummaryRes] = await Promise.all([
        authFetch('/api/v1/reports/dashboard'),
        authFetch('/api/v1/opportunities?open=true&limit=5'),
        authFetch('/api/v1/watchlist?limit=5'),
        authFetch('/api/v1/watchlist/summary')
      ]);

      const dashboardData = await dashboardRes.json();
      const opportunitiesData = await opportunitiesRes.json();
      const watchlistData = await watchlistRes.json();
      const watchlistSummary = await watchlistSummaryRes.json();

      const opportunities = opportunitiesData.data || [];

      setStats({
        totalCustomers: dashboardData.customers?.active || 0,
        directCustomers: dashboardData.customers?.direct || 0,
        distributorCustomers: dashboardData.customers?.distributors || 0,
        indirectCustomers: dashboardData.customers?.indirect || 0,
        openOpportunities: dashboardData.pipeline?.open_opportunities || 0,
        pipelineValue: dashboardData.pipeline?.pipeline_value || 0,
        weightedValue: dashboardData.pipeline?.weighted_pipeline_value || 0,
        // Orders are not in the pipeline figures; they are the order book.
        orderBookValue: dashboardData.orders?.order_book_value || 0,
        openOrderLines: dashboardData.orders?.open_lines || 0,
        watchlistCount: watchlistSummary.total_count || 0,
        watchlistOverdue: watchlistSummary.overdue_count || 0,
        watchlistRed: watchlistSummary.red_count || 0
      });

      setWatchlist(watchlistData.data || []);
      setRecentOpportunities(opportunities.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    };

    const handleFocus = () => {
      fetchStats();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchStats]);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.fullName}</p>
        </div>
        <WorldClocks />
        <div className="header-actions">
          <Link to="/pipeline/new" className="btn-primary">+ New Opportunity</Link>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon customers">C</div>
          <div className="stat-content">
            <h3>Customers</h3>
            <div className="stat-value stat-value-compact">
              {loading ? '...' : stats?.totalCustomers || 0}
            </div>
            <div className="customer-stat-breakdown">
              <Link to="/customers?status=active&channel=direct" className="customer-stat-row">
                <span>Direct</span>
                <strong>{loading ? '...' : stats?.directCustomers || 0}</strong>
              </Link>
              <Link to="/distributors" className="customer-stat-row">
                <span>Distributors</span>
                <strong>{loading ? '...' : stats?.distributorCustomers || 0}</strong>
              </Link>
              <Link to="/customers?status=active&channel=distributor" className="customer-stat-row">
                <span>Indirect</span>
                <strong>{loading ? '...' : stats?.indirectCustomers || 0}</strong>
              </Link>
            </div>
            <Link to="/customers" className="stat-link">View all</Link>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pipeline">P</div>
          <div className="stat-content">
            <h3>Open Opportunities</h3>
            <div className="stat-value">
              {loading ? '...' : stats?.openOpportunities || 0}
            </div>
            <Link to="/pipeline" className="stat-link">View pipeline</Link>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon value">V</div>
          <div className="stat-content">
            <h3>Pipeline Value</h3>
            <div className="stat-value">
              {loading ? '...' : formatCurrency(stats?.pipelineValue)}
            </div>
            <div className="stat-sub">
              Weighted: {loading ? '...' : formatCurrency(stats?.weightedValue)}
            </div>
            <div className="stat-sub">
              <Link to="/pipeline?tab=orders">
                Order book: {loading ? '...' : `${formatCurrency(stats?.orderBookValue)} (${stats?.openOrderLines || 0} line${stats?.openOrderLines === 1 ? '' : 's'})`}
              </Link>
            </div>
            <Link to="/reports?tab=sales" className="stat-link">Full report</Link>
          </div>
        </div>

        <div className={`stat-card ${stats?.watchlistOverdue > 0 ? 'alert' : ''}`}>
          <div className="stat-icon watchlist">W</div>
          <div className="stat-content">
            <h3>Watchlist</h3>
            <div className="stat-value">
              {loading ? '...' : stats?.watchlistCount || 0}
            </div>
            {stats?.watchlistOverdue > 0 && (
              <div className="stat-alert">{stats.watchlistOverdue} overdue</div>
            )}
            <Link to="/pipeline?tab=watchlist" className="stat-link">View watchlist</Link>
          </div>
        </div>
      </div>

      <div className="dashboard-row">
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Opportunities</h2>
            <Link to="/pipeline" className="view-all">View all</Link>
          </div>
          {recentOpportunities.length === 0 ? (
            <div className="empty-list">No opportunities yet</div>
          ) : (
            <div className="recent-list">
              {recentOpportunities.map(opp => (
                <Link key={opp.opportunity_id} to={`/pipeline/${opp.opportunity_id}`} className="recent-item">
                  <div className="item-main">
                    <span className="item-ref">{opp.opp_ref}</span>
                    <span className="item-title">{opp.title}</span>
                  </div>
                  <div className="item-meta">
                    <span className="item-customer">{opp.customer_name}</span>
                    <span className="item-stage">{opp.stage}</span>
                    <span className="item-value">{formatCurrency(opp.value_minor_units, opp.currency)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-section">
          <div className="section-header">
            <h2>Watchlist Alerts</h2>
            <Link to="/pipeline?tab=watchlist" className="view-all">View all</Link>
          </div>
          {watchlist.length === 0 ? (
            <div className="empty-list">No items on watchlist</div>
          ) : (
            <div className="watchlist-items">
              {watchlist.map(item => (
                <Link key={item.opportunity_id} to={`/pipeline/${item.opportunity_id}`} className="watchlist-item">
                  <div className={`priority-dot ${item.monitor_priority}`} />
                  <div className="item-content">
                    <div className="item-title">{item.title}</div>
                    <div className="item-details">
                      <span>{item.customer_name}</span>
                      <span>Review: {formatDateKey(item.monitor_review_date, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  </div>
                  {item.review_overdue && <span className="overdue-badge">Overdue</span>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-section quick-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <Link to="/customers/new?status=active" className="action-button">
            + New Customer
          </Link>
          <Link to="/pipeline/new" className="action-button">
            + New Opportunity
          </Link>
          <Link to="/pipeline" className="action-button">
            View Pipeline
          </Link>
          <Link to="/pipeline?tab=watchlist" className="action-button">
            Contracts Meeting
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
